import { useCallback, useLayoutEffect, useReducer, useRef } from 'react';
import { TextField } from '@mui/material';
import {
  formatPhoneInput,
  formatPhoneInputForRow,
  formatPhoneNumber,
  formatPhoneValueForRow,
} from '../helpers/phoneDisplay';

function digitCount(value) {
  return (value.match(/\d/g) || []).length;
}

function caretAfterDigits(value, count, sourceCaret = 0) {
  if (!count) {
    return sourceCaret > 0 && /^[+(]/.test(value) ? 1 : 0;
  }
  let digits = 0;
  for (let index = 0; index < value.length; index += 1) {
    if (/\d/.test(value[index]) && ++digits === count) return index + 1;
  }
  return value.length;
}

// Formatting punctuation is disposable. Delete the digit the user was trying
// to reach instead of immediately re-inserting a removed separator.
function removeAdjacentDigit(value, caret, backward) {
  let index = backward ? caret - 1 : caret;
  const direction = backward ? -1 : 1;
  while (index >= 0 && index < value.length && !/\d/.test(value[index])) index += direction;
  if (index < 0 || index >= value.length) return null;
  return { value: value.slice(0, index) + value.slice(index + 1), caret: index };
}

function PhoneTextField({
  value = '',
  onValueChange,
  onValueBlur,
  countryValue = '',
  header = 'Telefono',
  headers,
  row,
  inputRef: forwardedInputRef,
  inputProps,
  onKeyDown,
  onSelect,
  onPaste,
  onBlur,
  ...props
}) {
  const inputRef = useRef(null);
  const pendingCaret = useRef(null);
  const selectedRange = useRef(false);
  const [, refreshCaret] = useReducer((version) => version + 1, 0);
  const rowContext = Array.isArray(headers) && Array.isArray(row);
  const readOnly = Boolean(props.disabled || inputProps?.readOnly || props.InputProps?.readOnly
    || props.slotProps?.input?.readOnly || props.slotProps?.htmlInput?.readOnly);
  const formatInput = (next) => rowContext
    ? formatPhoneInputForRow(header, next, headers, row)
    : formatPhoneInput(next, countryValue);
  const displayValue = formatInput(value);

  const setInputRef = useCallback((input) => {
    inputRef.current = input;
    if (typeof forwardedInputRef === 'function') forwardedInputRef(input);
    else if (forwardedInputRef) forwardedInputRef.current = input;
  }, [forwardedInputRef]);

  useLayoutEffect(() => {
    const selection = pendingCaret.current;
    pendingCaret.current = null;
    if (selection && inputRef.current?.value === selection.value) {
      inputRef.current.setSelectionRange(selection.position, selection.position);
    }
  });

  const commitInput = (next, caret) => {
    selectedRange.current = false;
    const formatted = formatInput(next);
    pendingCaret.current = {
      value: formatted,
      position: formatted === next ? caret : caretAfterDigits(formatted, digitCount(next.slice(0, caret)), caret),
    };
    onValueChange?.(formatted);
    // Selection also needs restoring when deleting a separator produces the
    // same controlled value, so this update does not depend on the parent.
    refreshCaret();
  };

  const handleChange = (event) => {
    let next = event.target.value;
    let caret = event.target.selectionStart ?? next.length;
    const inputType = event.nativeEvent?.inputType || '';
    if (/^deleteContent(?:Backward|Forward)$/.test(inputType)
      && !selectedRange.current
      && next !== displayValue
      && digitCount(next) === digitCount(displayValue)
      && /^\+?[\d\s()._-]*$/.test(displayValue)) {
      const backward = inputType === 'deleteContentBackward';
      const deletion = removeAdjacentDigit(displayValue, caret + (backward ? 1 : 0), backward);
      if (deletion) {
        next = deletion.value;
        caret = deletion.caret;
      }
    }
    commitInput(next, caret);
  };

  const handleKeyDown = (event) => {
    onKeyDown?.(event);
    selectedRange.current = event.target.selectionStart !== event.target.selectionEnd;
    if (event.defaultPrevented || readOnly || event.ctrlKey || event.metaKey || event.altKey
      || !['Backspace', 'Delete'].includes(event.key)
      || event.target.selectionStart !== event.target.selectionEnd
      || !/^\+?[\d\s()._-]*$/.test(displayValue)) return;
    const backward = event.key === 'Backspace';
    const caret = event.target.selectionStart ?? displayValue.length;
    const adjacent = displayValue[backward ? caret - 1 : caret];
    if (!adjacent || !/[\s()._-]/.test(adjacent)) return;
    const deletion = removeAdjacentDigit(displayValue, caret, backward);
    if (!deletion) return;
    event.preventDefault();
    commitInput(deletion.value, deletion.caret);
  };

  const handleSelect = (event) => {
    selectedRange.current = event.target.selectionStart !== event.target.selectionEnd;
    onSelect?.(event);
  };

  const handlePaste = (event) => {
    onPaste?.(event);
    if (event.defaultPrevented || readOnly) return;
    const text = event.clipboardData?.getData('text');
    if (typeof text !== 'string') return;
    const start = event.target.selectionStart ?? displayValue.length;
    const end = event.target.selectionEnd ?? start;
    event.preventDefault();
    commitInput(displayValue.slice(0, start) + text + displayValue.slice(end), start + text.length);
  };

  const handleBlur = (event) => {
    const formatted = rowContext
      ? formatPhoneValueForRow(header, event.target.value, headers, row)
      : formatPhoneNumber(event.target.value, countryValue);
    if (onValueBlur) onValueBlur(formatted);
    else if (formatted !== value) onValueChange?.(formatted);
    onBlur?.(event);
  };

  return (
    <TextField
      {...props}
      type="tel"
      value={displayValue}
      inputRef={setInputRef}
      inputProps={{ inputMode: 'tel', ...inputProps }}
      onChange={handleChange}
      onKeyDown={handleKeyDown}
      onSelect={handleSelect}
      onPaste={handlePaste}
      onBlur={handleBlur}
    />
  );
}

export default PhoneTextField;
